import { FormEventHandler, useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import pick from "lodash/pick";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { Toast } from "@sscs/ui";
import { useAppDispatch } from "Hooks";
import * as yup from "Utils/yup";
import { addOverride, removeRevertOverride } from "../store/policyConfigSlice";
import { Policy, PolicyOverride, Reason } from "../types";
import { generateReasons } from "../utils";

type EditPolicyFormData = {
  id: string;
  enabled: "enabled" | "disabled";
  blocker: "pass" | "fail";
  reason: string;
};

const EditPolicyFormSchema: yup.SchemaOf<EditPolicyFormData> = yup.object({
  id: yup.string().required(),
  enabled: yup
    .mixed<EditPolicyFormData["enabled"]>()
    .oneOf(["enabled", "disabled"])
    .required(),
  blocker: yup
    .mixed<EditPolicyFormData["blocker"]>()
    .oneOf(["pass", "fail"])
    .required(),
  reason: yup
    .string()
    .max(500)
    .required("Please specify a reason for the policy change"),
});

type UseEditPolicyFormProps = {
  policy: Policy;
  policyOverrideState: PolicyOverride;
  derivedScanLevel: number;
  onClose: () => void;
};

type UseEditPolicyForm = {
  errors: FieldErrors<EditPolicyFormData>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  methods: UseFormReturn<EditPolicyFormData>;
  inheritedEnabled: boolean;
  inheritedBlocker: boolean;
  isConfirmButtonDisabled: boolean;
  isReasonFieldDisabled: boolean;
  isTryingToUndo: boolean;
  isTryingToUndoToOverride: boolean;
  isTryingToRevert: boolean;
  isDisabledPolicyModified: boolean;
  reasons: Reason[];
};

const useEditPolicyForm = ({
  policy,
  policyOverrideState,
  derivedScanLevel,
  onClose,
}: UseEditPolicyFormProps): UseEditPolicyForm => {
  const dispatch = useAppDispatch();

  const policyDefaults = {
    ...policy.inherited,
    ...pick(policy.override, ["blocker", "enabled", "reason"]),
    ...pick(policyOverrideState, ["blocker", "enabled", "reason"]),
  };

  const inheritedEnabled = policy.inherited.enabled;
  let inheritedBlocker = policy.inherited.blocker;

  // If levels are enabled, blocker is determined by level, not by inherited value
  // Unless inherited value is user override in which case levels don't matter
  if (
    policy.rl_level > 0 &&
    derivedScanLevel > 0 &&
    !policy.inherited.is_user_override
  ) {
    if (policy.rl_level <= derivedScanLevel) inheritedBlocker = true;
    else inheritedBlocker = false;
  }

  const defaultValues: EditPolicyFormData = useMemo(
    () => ({
      id: policy.id,
      enabled: policyDefaults.enabled ? "enabled" : "disabled",
      blocker: policyDefaults.blocker ? "fail" : "pass",
      reason: policyDefaults.reason,
    }),
    [
      policy.id,
      policyDefaults.enabled,
      policyDefaults.blocker,
      policyDefaults.reason,
    ],
  );

  const methods = useForm<EditPolicyFormData>({
    resolver: yupResolver(EditPolicyFormSchema),
    defaultValues,
  });

  const {
    formState: { errors },
    handleSubmit,
    getFieldState,
    setValue,
  } = methods;

  const isEnabledFieldValue = methods.watch("enabled") === "enabled";
  const isBlockerFieldValue = methods.watch("blocker") === "fail";

  const { isDirty: isEnabledFieldDirty } = getFieldState(
    "enabled",
    methods.formState,
  );

  const { isDirty: isBlockerFieldDirty } = getFieldState(
    "blocker",
    methods.formState,
  );

  const { isDirty: isReasonFieldDirty } = getFieldState(
    "reason",
    methods.formState,
  );

  const isConfigurationDirty = isEnabledFieldDirty || isBlockerFieldDirty;
  const isFormDirty = isConfigurationDirty || isReasonFieldDirty;

  const samesAsInherited =
    inheritedBlocker === isBlockerFieldValue &&
    inheritedEnabled === isEnabledFieldValue;
  const samesAsOverride =
    policy.override?.blocker === isBlockerFieldValue &&
    policy.override?.enabled === isEnabledFieldValue;
  const isReasonDisabled =
    (!isConfigurationDirty && !policyDefaults.reason) ||
    samesAsInherited ||
    samesAsOverride ||
    (!inheritedEnabled && !isEnabledFieldValue);

  useEffect(() => {
    // If reason was present from before, and user changed configuration
    // reason needs to be reset to user is forced to put in a new (correct) one
    if (policyDefaults.reason && !isReasonFieldDirty && isConfigurationDirty) {
      setValue("reason", "");
    }
  }, [
    policyDefaults.reason,
    isConfigurationDirty,
    isReasonFieldDirty,
    setValue,
  ]);

  const onSubmit: SubmitHandler<EditPolicyFormData> = async (formData) => {
    // clone the form data so changes to form data won't affect us after reset
    const data = { ...formData };
    try {
      dispatch(
        addOverride({
          ...data,
          blocker: data.blocker === "fail",
          enabled: data.enabled === "enabled",
        }),
      );
      dispatch(removeRevertOverride(data.id));

      onClose();

      Toast.show({
        title: "Policy updated",
        type: "success",
      });
    } catch {
      Toast.show({
        title: `Could not update policy`,
        type: "error",
      });
    }
  };

  const reasons = generateReasons({
    inheritedBlocker,
    inheritedEnabled,
    currentBlocker: isBlockerFieldValue,
    currentEnabled: isEnabledFieldValue,
  });

  const isTryingToUndoToInherited =
    isFormDirty && !policy.override && samesAsInherited;
  const isTryingToUndoToOverride = isFormDirty && samesAsOverride;

  const isTryingToUndo = isTryingToUndoToOverride || isTryingToUndoToInherited;
  const isTryingToRevert = isFormDirty && !!policy.override && samesAsInherited;

  return {
    onSubmit: handleSubmit(onSubmit),
    errors,
    methods,
    inheritedEnabled,
    inheritedBlocker,
    isReasonFieldDisabled: isReasonDisabled,
    isConfirmButtonDisabled: isReasonDisabled,
    isTryingToUndo,
    isTryingToUndoToOverride,
    isTryingToRevert,
    isDisabledPolicyModified:
      isFormDirty && !inheritedEnabled && !isEnabledFieldValue,
    reasons,
  };
};

export default useEditPolicyForm;
